import gsap from 'gsap';
import AbstractUnit from './AbstractUnit';
import { COLOR_SCHEMES, COLOR_THEMES } from '../../../../constants/colors';
import {
    REF_SIZE,
    REF_SIZE_PT, SIZE_OPTIONS, UNIT
} from '../../../../constants/size';
import GradientDissolveRect from '../../objects/GradientDissolveRect';
import PlaneDecorRectangle from '../../objects/PlaneDecorRectangle';
import { frameFlowersConfig } from '../../objects/configs/PlaneDecorConfig';
import Text3D from '../../objects/Text3D';
import LinesSet from '../../objects/LinesSet';
import { MaterialsFactory } from '../../factories/MaterialsFactory';
import { TexturesStorage } from '../../factories/TexturesStorage';
import { GeometriesFactory } from '../../factories/GeometriesFactory';
import { ActionTypes } from '../../../../constants/ActionTypes';
import { _openLinkBlank } from '../../../../utils/browse';
import { linear, clamp01 } from '../../../../utils/progress';
import { getArrowButton } from './utils/ui';

const THREE = require('three');

const getPreviewMaterial = (type, data, texture, transparent = true) => MaterialsFactory.create({
    map: texture,
    transparent
});

const arrowOverFunc = (button, webgl) => {
    const duration = 0.35;

    gsap.killTweensOf(button.view.position);
    gsap.killTweensOf(button.position);
    gsap.to(button.view.position, {
        duration,
        x: 0.002 * UNIT.zSize
    });
    gsap.to(button.position, {
        duration,
        z: 0.042 * UNIT.zSize
    });

    const moveFunc = (evt) => {
        button.updateMatrixWorld();

        const { camera } = webgl;
        const vectorMouse = evt.data.global;
        let vector = new THREE.Vector3();
        const mtx = new THREE.Matrix4();

        vector.setFromMatrixPosition(button.hitArea.matrixWorld);

        vector = vector.applyMatrix4(camera.matrixWorldInverse).applyMatrix4(mtx.copy(camera.projectionMatrix));
        vector.set(
            (vectorMouse.x - vector.x) / 1.2,
            (vectorMouse.y - vector.y) / 1.2,
            0
        );

        gsap.killTweensOf(button.mesh.position);
        gsap.to(button.mesh.position, {
            duration: 0.2,
            ease: 'sine.Out',
            x: vector.x * 0.15 * UNIT.zSize,
            y: vector.y * 0.15 * UNIT.zSize
        });
    };

    button.moveFunc = moveFunc;
    button.hitArea.on('mousemove', moveFunc);
};
const arrowOutFunc = (button) => {
    const duration = 0.55;

    gsap.killTweensOf(button.view.position);
    gsap.killTweensOf(button.mesh.position);
    gsap.killTweensOf(button.position);
    gsap.to(button.view.position, {
        duration,
        x: 0
    });
    gsap.to(button.position, {
        duration,
        z: 0
    });
    gsap.to(button.mesh.position, {
        duration,
        x: 0,
        y: 0
    });
};

export const ARFilterPreviewEvent = {
    PREV: 'arFilterPreviewNext',
    NEXT: 'arFilterPreviewPrev',
    SOCIAL: 'arFilterSocialOutbound'
};

class ARFilterPreviewUnit extends AbstractUnit {
    _setup(data, stgs, webgl) {
        super._setup(data, stgs, webgl);

        const {
            scheme, theme, title, titlePlane
        } = this.data;

        const {
            getTitleMetrics,
            getSocialMetrics
        } = stgs;

        this.hasResize = true;
        this.clickable = true;
        this.canFocusAt = true;
        this.actionCategory = 'Masks Preview';
        this.actionTypes = [ActionTypes.NEXT_MASK, ActionTypes.PREV_MASK, ActionTypes.CURRENT_MASK_LINK];
        this.hasProgressAnimation = true;
        this.isAfterChangeSchemePoint = false;

        this._title = titlePlane ?? title;
        this._getTitleMetrics = getTitleMetrics;
        this._getSocialMetrics = getSocialMetrics;
        this._webgl = webgl;

        this._theme = theme;
        this._scheme = scheme;
        this._schemeConfig = COLOR_SCHEMES[scheme];
        this._color = COLOR_THEMES[theme].text;

        // Default
        this._orientation = SIZE_OPTIONS.LANDSCAPE;
    }

    async _constructView(data, webgl, stgs, factory, orientation) {
        super._constructView(data, webgl, stgs, factory, orientation);

        const { bg } = this._schemeConfig;
        const sizesScale = UNIT.sizesScaleRatio;
        const {
            preview
        } = this.data;
        const {
            options
        } = preview;

        this.state = {
            length: options.length,
            options,
            currentIndex: 0,
            changed: true,
            currentIndexChanged: true
        };

        const titleMesh = this._getTextMesh(
            this._getTitleMetrics(orientation),
            this._title
        );
        const linesTop = new LinesSet(this._color, 96, 12, 0.4, 2);

        linesTop.scale.set(sizesScale, sizesScale, sizesScale);

        const dissolveRect = new GradientDissolveRect(bg, false);

        const frameGroup = new THREE.Group();
        const frameDecorMesh = new PlaneDecorRectangle(frameFlowersConfig);

        dissolveRect.scale.set(1.6, 2, 1);
        frameDecorMesh.position.z = 0.03 * UNIT.zSize;
        titleMesh.position.z = 0.03 * UNIT.zSize;

        frameGroup.add(frameDecorMesh);
        frameGroup.add(titleMesh);
        frameGroup.add(linesTop);

        this.add(dissolveRect);
        this.add(frameGroup);

        this.titleMesh = titleMesh;
        this.linesTop = linesTop;
        this.mainView = frameGroup;

        this.mainView.layers.set(this._layerChannel);

        this._shiftRenderOrder = 20;

        this.renderOrder = 1 + this._shiftRenderOrder;
        dissolveRect.renderOrder = 1 + this._shiftRenderOrder;
        titleMesh.renderOrder = 3 + this._shiftRenderOrder;
        frameGroup.renderOrder = 2 + this._shiftRenderOrder;
        frameDecorMesh.renderOrder = 2 + this._shiftRenderOrder;

        this.dissolveRect = dissolveRect;
        this.frameGroup = frameGroup;
        this.frameDecorMesh = frameDecorMesh;

        // UI Layers
        await this._addUILayers(frameGroup, webgl, orientation);

        this._configActions();

        // Load preview
        await this._loadPreview(frameGroup);
    }

    async _addUILayers(frameGroup, webgl, orientation) {
        const {
            links, ctaLabel
        } = this.data;

        // - step 0: select an option
        const step0Layer = new THREE.Group();
        const prevButton = await getArrowButton();
        const nextButton = await getArrowButton();

        prevButton.scale.x = -1;
        prevButton.position.x = -prevButton.width * 0.61;
        nextButton.position.x = nextButton.width * 0.61;

        step0Layer.add(nextButton);
        step0Layer.add(prevButton);

        // - step 1: links
        const step1Layer = new THREE.Group();
        const linkElements = links.map((link, index, arr) => {
            const obj3D = this._getSocialButton(
                `${index < 1 ? '' : '^  '}${link.label}${index < arr.length - 1 ? '  |' : ''}`,
                this._color,
                webgl,
                orientation
            );

            obj3D.layers.set(this._layerChannel);
            obj3D.textMesh.renderOrder = 33;
            obj3D.view.renderOrder = 33;
            obj3D.hitArea.renderOrder = 35;

            step1Layer.add(obj3D);

            return { obj3D, ...link };
        });
        // + CTA
        const ctaMesh = this._getTextMesh(
            this._getTitleMetrics(orientation),
            ctaLabel
        );

        ctaMesh.renderOrder = 33;

        step1Layer.add(ctaMesh);

        step0Layer.renderOrder = 40 + this._shiftRenderOrder;
        prevButton.renderOrder = 42 + this._shiftRenderOrder;
        prevButton.view.renderOrder = 42 + this._shiftRenderOrder;
        prevButton.mesh.renderOrder = 42 + this._shiftRenderOrder;
        prevButton.hitArea.renderOrder = 43 + this._shiftRenderOrder;
        nextButton.renderOrder = 44 + this._shiftRenderOrder;
        nextButton.view.renderOrder = 44 + this._shiftRenderOrder;
        nextButton.mesh.renderOrder = 44 + this._shiftRenderOrder;
        nextButton.hitArea.renderOrder = 45 + this._shiftRenderOrder;
        step1Layer.renderOrder = 30 + this._shiftRenderOrder;

        step0Layer.position.z = 0.03 * UNIT.zSize;
        step1Layer.position.z = 0.006 * UNIT.zSize;

        prevButton.layers.set(this._layerChannel);
        nextButton.layers.set(this._layerChannel);
        step0Layer.layers.set(this._layerChannel);
        step1Layer.layers.set(this._layerChannel);
        frameGroup.layers.set(this._layerChannel);

        frameGroup.add(step0Layer);
        frameGroup.add(step1Layer);

        this.step0Layer = step0Layer;
        this.step1Layer = step1Layer;
        this.linkElements = linkElements;
        this.prevButton = prevButton;
        this.nextButton = nextButton;
        this.ctaMesh = ctaMesh;
    }

    async _loadPreview(frameGroup) {
        const {
            preview, geometry
        } = this.data;
        const {
            type, options
        } = preview;
        const geom = await GeometriesFactory.create(geometry.type, geometry);
        const previewMeshes = [];

        this._previewGeometry = geom;

        return Promise.all(options.map(async (option) => {
            const texture = await TexturesStorage.getTexture({ options: option.textures });
            const previewMesh = new THREE.Mesh(geom, getPreviewMaterial(type, preview, texture));

            frameGroup.add(previewMesh);
            previewMeshes.push(previewMesh);
        })).then(() => {
            this.previewMeshes = previewMeshes;
        });
    }

    _configActions() {
        this.mainClickTarget = [
            ...this.linkElements.map((link) => link.obj3D.hitArea),
            this.prevButton.hitArea,
            this.nextButton.hitArea
        ];
        this.clickActions = [
            ...this.linkElements.map((link) => link.action),
            {
                key: ActionTypes.PREV_MASK
            },
            {
                key: ActionTypes.NEXT_MASK
            }
        ];
        this.focusMap = new Map();

        this.focusMap.set(ActionTypes.PREV_MASK, this.prevButton);
        this.focusMap.set(ActionTypes.NEXT_MASK, this.nextButton);

        this.linkElements.forEach((link) => {
            this.focusMap.set(`${link.action.key}-${link.action.social}`, link.obj3D);
        });
    }

    start() {
        super.start();

        if (this.clickable) {
            this._updateState();
            this._addEventListeners();
        }
    }

    _handleReady() {
        super._handleReady();

        if (this.clickable && this._started) {
            this._updateState(true);
            this._addEventListeners();
        }
    }

    _getTextMesh(metrics, text) {
        if (metrics.isUpperCase) text = text.toUpperCase();

        return new Text3D({
            ...metrics,
            color: this._color
        }, text, this.webgl);
    }

    _getTextButton(text, color, webgl, orientation) {
        const group = new THREE.Group();
        const textMesh = new Text3D({
            ...this._getTitleMetrics(orientation),
            color
        }, text, webgl);

        let geom = new THREE.PlaneBufferGeometry(100, 2);
        const material = MaterialsFactory.create({
            color
        });

        material.fog = true;

        const line = new THREE.Mesh(geom, material);

        geom = new THREE.PlaneBufferGeometry(100, 100);
        const hitArea = new THREE.Mesh(geom, MaterialsFactory.create({
            transparent: true,
            opacity: 0
        }));

        hitArea.position.z = 0.006 * UNIT.zSize;

        textMesh.renderOrder = 2;
        line.renderOrder = 2;
        hitArea.renderOrder = 20;

        group.add(textMesh);
        group.add(line);
        group.add(hitArea);

        group.textMesh = textMesh;
        group.line = line;
        group.hitArea = hitArea;

        return group;
    }

    _getSocialButton(text, color, webgl, orientation) {
        const group = new THREE.Group();
        const view = new THREE.Group();
        const textMesh = new Text3D({
            ...this._getSocialMetrics(orientation),
            color
        }, text, webgl);

        const geom = new THREE.PlaneBufferGeometry(100, 100);
        const hitArea = new THREE.Mesh(geom, MaterialsFactory.create({
            transparent: true,
            opacity: 0
        }));

        hitArea.position.z = 0.001 * UNIT.zSize;

        view.add(textMesh);
        group.add(view);
        group.add(hitArea);

        group.textMesh = textMesh;
        group.mesh = textMesh;
        group.view = view;
        group.hitArea = hitArea;

        return group;
    }

    resize(winWidth, winHeight, orientation, force, pixelRatio) {
        const prevOrientation = this._orientation;

        super.resize(winWidth, winHeight, orientation, force, pixelRatio);

        this.titleMesh.setMetrics({
            ...this._getTitleMetrics(orientation)
        });

        if (this.startButton) {
            this.startButton.textMesh.setMetrics({
                ...this._getTitleMetrics(orientation)
            });
            this.stopButton.textMesh.setMetrics({
                ...this._getTitleMetrics(orientation)
            });
            this.ctaMesh.setMetrics({
                ...this._getTitleMetrics(orientation)
            });

            const updateButton = (btn) => {
                btn.textMesh.position.y = 0.7 * btn.textMesh.textHeight;
                btn.line.scale.x = btn.textMesh.textInnerWidth / 100;
                btn.hitArea.scale.x = btn.line.scale.x;
                btn.hitArea.scale.y = (1.65 * btn.textMesh.textHeight) / 100;
                btn.hitArea.position.y = 0.15 * btn.textMesh.textHeight;
            };

            updateButton(this.startButton);
            updateButton(this.stopButton);
        }

        if (this.linkElements) {
            // Only for 2 links. Fix for more
            this.linkElements.forEach((linkEl, index, arr) => {
                const { obj3D } = linkEl;
                const isLeft = index < arr.length / 2;

                obj3D.textMesh.setMetrics({
                    ...this._getSocialMetrics(orientation),
                    // eslint-disable-next-line no-nested-ternary
                    align: arr.length < 2 ? 'center' : (isLeft ? 'right' : 'left')
                });

                if (arr.length > 1) obj3D.position.x = (isLeft ? -1 : 1) * obj3D.textMesh.textWidth * 0.5;
                obj3D.hitArea.scale.x = obj3D.textMesh.textWidth / 100;
                obj3D.hitArea.scale.y = (1.75 * obj3D.textMesh.textHeight) / 100;
                obj3D.hitArea.position.y = 0.05 * obj3D.textMesh.textHeight;
            });
        }

        if (this.objectsGroup2 && this.objectsGroup) this.objectsGroup2.position.z = this.objectsGroup.position.z;

        this.dissolveRect.viewportWidth = winWidth * pixelRatio;
        this.dissolveRect.viewportHeight = winHeight * pixelRatio;

        if (orientation === prevOrientation && !force) return;

        let frameScale;
        let frameY = 0;
        let textDY = 0;
        let linesDY = 0;
        let h = 0;
        const sizesScale = UNIT.sizesScaleRatio;

        if (orientation === SIZE_OPTIONS.LANDSCAPE) {
            h = REF_SIZE.h;
            frameScale = 0.57;
            frameY = 0.016 * h * sizesScale;
            textDY = 0.73 * h * sizesScale;
            linesDY = 0.05 * h * sizesScale;
        } else {
            h = REF_SIZE_PT;
            frameScale = 1.4;
            frameY = 0.1 * h * sizesScale;
            textDY = 0.58 * h * sizesScale;
            linesDY = 0.1 * h * sizesScale;
        }

        this.titleMesh.position.y = frameY + textDY;
        this.linesTop.position.y = this.titleMesh.position.y - linesDY;

        this.frameGroup.scale.set(frameScale, frameScale, 1);
        this.frameGroup.position.z = -this.mainViewZ;

        this._updateUIPosition();

        this.dissolveRect.position.set(
            0,
            frameY,
            -this.mainViewZ - 0.15 * UNIT.zSize
        );
    }

    _updateUIPosition(orientation) {
        if (this.step1Layer) {
            let layers12Y = 0;
            let layers0DY = 0;
            let layers3DY = 0;
            let ctaMeshDY = 0;
            let h = 0;
            const sizesScale = UNIT.sizesScaleRatio;

            if (orientation === SIZE_OPTIONS.LANDSCAPE) {
                h = REF_SIZE.h;
                layers12Y = -0.75 * h * sizesScale;
                layers0DY = 0.1 * h * sizesScale;
                layers3DY = -0.1 * h * sizesScale;
                ctaMeshDY = -0.15 * 0.15 * h * sizesScale;
            } else {
                h = REF_SIZE_PT.h;
                layers12Y = -0.65 * h * sizesScale;
                layers0DY = 0.1 * h * sizesScale;
                layers3DY = -0.1 * h * sizesScale;
                ctaMeshDY = -0.15 * 0.15 * h * sizesScale;
            }

            this.step0Layer.position.y = layers12Y + layers0DY;
            this.step1Layer.position.y = layers12Y + layers3DY;
            this.ctaMesh.position.y = this.ctaMesh.textHeight - ctaMeshDY;
        }
    }

    _updateAnimation(newProgress) {
        this.dissolveRect.opacity = 1 - clamp01(linear(newProgress, -0.5, -0.35));
        this.frameDecorMesh.opacity = clamp01(linear(newProgress, -1.2, -0.9));
        this.frameGroup.position.y = UNIT.sizesScaleRatio * (100 - 2400 * clamp01(linear(newProgress, -0.7, -0.15)));
    }

    _updateState(force = false) {
        if (!this.state.changed && !force) return;

        if (this.previewMeshes && (this.state.currentIndexChanged || force)) {
            const { currentIndex } = this.state;

            this.previewMeshes.forEach((preview, index) => {
                gsap.killTweensOf(preview.material);
                gsap.to(preview.material, {
                    duration: 0.35,
                    opacity: index === currentIndex ? 1 : 0
                });
            });
        }

        this.state.changed = false;
        this.state.currentIndexChanged = false;
    }

    focusAt(actionElemIdOrName) {
        if (!this.clickable) return;

        const target = this.focusMap.get(actionElemIdOrName);

        if (!target || !target.overFunc) return;

        target.overFunc();
    }

    blur(actionElemIdOrName) {
        if (!this.clickable) return;

        const target = this.focusMap.get(actionElemIdOrName);

        if (!target || !target.overFunc) return;

        target.outFunc();
    }

    _addEventListeners() {
        if (this._listenersAdded) return;

        if (this.prevButton) {
            const addArrowListeners = (button) => {
                const webgl = this._webgl;
                const overFunc = () => {
                    arrowOverFunc(button, webgl);
                };
                const outFunc = () => {
                    arrowOutFunc(button, webgl);
                };

                button.hitArea.on('mouseover', overFunc);
                button.hitArea.on('mouseout', outFunc);
                button.overFunc = overFunc;
                button.outFunc = outFunc;
            };

            addArrowListeners(this.prevButton);
            addArrowListeners(this.nextButton);

            this.linkElements.forEach((link) => {
                addArrowListeners(link.obj3D);
            });

            this._listenersAdded = true;
        }
    }

    callAction(action) {
        console.log(action);
        super.callAction(action);

        if (!action) return;

        this._callAction(action);
        this._updateState();
    }

    _callAction(action) {
        const { key, social } = action;

        console.log(action);

        switch (key) {
            case ActionTypes.PREV_MASK_REQUEST:
            case ActionTypes.PREV_MASK:
                this._switchToPrev();
                if (this.state.changed) {
                    console.log(this.state.isFilterOn ? 'Примерить маску' : 'Превью', this.currentOptionConfig);
                    this.dispatchEvent({
                        type: ActionTypes.PREV_MASK,
                        category: this.actionCategory
                    });
                }

                break;

            case ActionTypes.NEXT_MASK_REQUEST:
            case ActionTypes.NEXT_MASK:
                this._switchToNext();
                if (this.state.changed) {
                    console.log(this.state.isFilterOn ? 'Примерить маску' : 'Превью', this.currentOptionConfig);
                    this.dispatchEvent({
                        type: ActionTypes.NEXT_MASK,
                        category: this.actionCategory
                    });
                }

                break;

            case ActionTypes.CURRENT_MASK_LINK_REQUEST:
            case ActionTypes.CURRENT_MASK_LINK:
                if (this.currentOptionMaskConfig) {
                    const socialConfig = this.currentOptionMaskConfig.social;
                    const href = socialConfig[social];

                    _openLinkBlank(href);
                    this.dispatchEvent({
                        type: ActionTypes.CURRENT_MASK_LINK,
                        category: this.actionCategory,
                        social,
                        href
                    });
                }

                break;

            default:
                break;
        }
    }

    _switchToPrev() {
        console.log('PREV');
        const { length } = this.state;
        let { currentIndex } = this.state;

        currentIndex -= 1;
        if (currentIndex < 0) currentIndex = length - 1;

        this.state.currentIndex = currentIndex;
        this.state.changed = true;
        this.state.currentIndexChanged = true;
    }

    _switchToNext() {
        const { length } = this.state;
        let { currentIndex } = this.state;

        currentIndex += 1;
        if (currentIndex > length - 1) currentIndex = 0;

        this.state.currentIndex = currentIndex;
        this.state.changed = true;
        this.state.currentIndexChanged = true;
    }

    get currentOptionConfig() {
        if (this.state.length < 1) return null;

        return this.state.options[this.state.currentIndex];
    }

    get currentOptionMaskConfig() {
        if (this.currentOptionConfig) return this.data.masks[this.currentOptionConfig.key];

        return null;
    }

    get objectsZ() {
        return this.mainViewZ;
    }
}

export default ARFilterPreviewUnit;
